<script setup lang="ts">
import { computed } from 'vue';
import FilterMenuItem from '@/components/FilterMenuItem.vue';
import { useGlobal, useMap, usePois } from '@/composables';
import type { Markets, Area } from '@/types';

const categories = [
  'lhoist',
  'agriculture',
  'civil-engineering',
  'construction',
  'flue-gas-treatment',
  'glass',
  'non-ferrous',
  'paper',
  'steel',
  'water',
] as const;

const { selectedCategory, selectedPoi, pois } = usePois();
const { zoomTo, apply, focusOnArea } = useMap();
const { isPoiFocused } = useGlobal();

const isActive = computed(() => (category: Markets[number]) => selectedCategory.value === category);
const SCALING_FACTOR = 9999;

const handleClick = (category: Markets[number]) => {
  // If the category is already selected, needed to zoom out action
  const isUnclicked = selectedCategory.value === category;

  selectedCategory.value = (() => (selectedCategory.value === category ? null : category))();
  if (isPoiFocused.value) {
    apply(() => {
      selectedPoi.value = null;
      isPoiFocused.value = false;
      zoomTo(SCALING_FACTOR);
    });
  }

  if (isUnclicked) {
    apply(() => {
      zoomTo(SCALING_FACTOR);
    });
    return;
  }

  // Find all POIs that match the selected category
  const matchingPois = pois.value.filter((poi) => poi.category === category);

  if (matchingPois.length > 0) {
    // Calculate the bounding box that encompasses all matching POIs
    const minLeft = Math.min(...matchingPois.map((poi) => poi.left));
    const maxLeft = Math.max(...matchingPois.map((poi) => poi.left + poi.width));
    const minTop = Math.min(...matchingPois.map((poi) => poi.top));
    const maxTop = Math.max(...matchingPois.map((poi) => poi.top + poi.height));

    // Create an Area object from the bounding box attributes
    const area: Area = {
      left: minLeft,
      top: minTop,
      width: maxLeft - minLeft,
      height: maxTop - minTop,
    };

    // Focus on the calculated area
    apply(
      () => {
        focusOnArea(area);
      },
      { saveState: true },
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-end gap-1 lg:gap-2">
    <FilterMenuItem
      @click="handleClick(category)"
      v-for="category in categories"
      :key="category"
      :active="isActive(category)"
      :category="category"
    />
  </div>
</template>
